/**
 * Floor Planner — the plan from above, drawn like a blueprint.
 *
 * Rooms are rectangles on a sheet: draw new ones, select (Shift / Ctrl-click
 * for several), drag to move, pull a handle to resize, type exact sizes, name
 * them, set ceiling heights, delete. A room dragged up to another catches on
 * its wall and joins it. Every rule (no overlaps, joined or two walls apart)
 * lives in calculations/plan; a drag simply stops where it may not go.
 *
 * With `maxRooms` of 1 (Advanced mode) there is no Draw tool: a blank sheet
 * shows the create wizard (CreateRoomWizard), and a floating "Design in 3D"
 * button goes on to 3D. The multi-room logic is not removed, only not reachable.
 *
 * The selected room is edited from a small toolbar beside it (RoomToolbar) —
 * Space, Size, Ceiling, Finish — and the pencil by its name; each opens the
 * room editor (RoomEditDialog) on that part. A shared wall picked on the sheet
 * gets its own panel at the top right.
 *
 * Pure SVG — no three.js. The sheet is a true top view: +x (a room's length)
 * runs right, +z (its width) runs down, so a room's front wall is its top edge.
 *
 * The host owns the plan and its undo history; this component reports each
 * finished edit through `onCommit`.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Box, Maximize, MousePointer2, PenLine, PencilRuler, Redo2, Undo2 } from 'lucide-react';
import {
  FloorPlan, PlanRect, PlanRoom, PlanFace, PLAN_FACES, MovingEdges, PlanResult,
  WALL_GAP_FT, ROOM_LIMITS, isWallOpen, sharedStretches, sharedWallKey, toggleOpenWall,
  openingsInStretch, openWallBehind, closeWallBehind,
  addRoom, applyStarterFinish, deleteRooms, heightForNewRoom, moveRooms, nudgeRooms, placementIssue, planBounds,
  rectOfRoom, setCeilingProfile, setRoomRect, sheetAlong, snapLinesFor, snapMove, snapPoint, snapResize, withStaging,
} from '../../../calculations/plan';
import { Opening, doorHinge, doorSwing } from '../../../types/sections';
import { ceilingGeometry } from '../../../calculations/ceiling/profile';
import { formatFeetInches } from '../SectionEditOverlay';
import { DesignerHeader, DesignerBrand } from '../chrome/DesignerHeader';
import { IconTextButton } from '../chrome/IconTextButton';
import { usePhoneSized } from '../chrome/usePhoneSized';
import { RoomToolbar } from './RoomToolbar';
import { RoomEditDialog, roomSteps } from './RoomEditDialog';
import { RoomStep } from './roomSteps';
import { CreateRoomWizard, CreateRoomForm } from './CreateRoomWizard';
import { DESIGNER_STRINGS as S } from '../strings';
import '../designer.css';

const P = S.planner;

/** Where a window or door on a shared wall is: its sheet span and sill height. */
const spotOf = (e: { from: number; to: number; opening: { y: number } }) => `${e.from.toFixed(3)}:${e.to.toFixed(3)}:${e.opening.y.toFixed(3)}`;

// Blueprint palette.
const SHEET = '#153e6e';
const LINE = '#eaf2ff';
const LINE_SOFT = 'rgba(234,242,255,0.55)';
const SELECT = '#FEB100';
const BAD = '#ff6b5e';
const FONT = '"Courier New", ui-monospace, Consolas, monospace';

const HANDLE_PX = 9;
const SNAP_PX = 10;
const CLICK_PX = 4;
const GRID_FT = 0.5;
const INCH = 1 / 12;
const MIN_SCALE = 3;
const MAX_SCALE = 140;
/** A shared wall's panel at the top right; on a phone, a sheet across the bottom, this much of the height. */
const PANEL_WIDTH = 278;
const PHONE_PANEL_FRACTION = 0.45;
/** Kept clear for the Design in 3D button: on a phone at the top of the sheet, else at the bottom. */
const CTA_SPACE_PX = 58;
/** On a phone, room below the rooms for the toolbar lying flat. */
const PHONE_TOOLBAR_PX = 84;

type Tool = 'select' | 'draw';
type HandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
const HANDLES: { id: HandleId; fx: number; fz: number; cursor: string }[] = [
  { id: 'nw', fx: 0, fz: 0, cursor: 'nwse-resize' }, { id: 'n', fx: 0.5, fz: 0, cursor: 'ns-resize' },
  { id: 'ne', fx: 1, fz: 0, cursor: 'nesw-resize' }, { id: 'e', fx: 1, fz: 0.5, cursor: 'ew-resize' },
  { id: 'se', fx: 1, fz: 1, cursor: 'nwse-resize' }, { id: 's', fx: 0.5, fz: 1, cursor: 'ns-resize' },
  { id: 'sw', fx: 0, fz: 1, cursor: 'nesw-resize' }, { id: 'w', fx: 0, fz: 0.5, cursor: 'ew-resize' },
];
const edgesOf = (h: HandleId): MovingEdges => ({ left: h.includes('w'), right: h.includes('e'), front: h.includes('n'), back: h.includes('s') });

interface View { scale: number; ox: number; oy: number }

export interface FloorPlannerProps {
  plan: FloorPlan;
  /** A finished edit. `coalesceKey` marks edits that belong to one undo step (typing in a field, nudging). */
  onCommit: (plan: FloorPlan, coalesceKey?: string) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  /** The room last clicked: a new room takes its height, and Back to 3D opens it. */
  focusRoomId: string | null;
  onFocusRoom: (id: string) => void;
  /** Back to the 3D view. Not offered while the plan has no rooms. */
  onClose: () => void;
  headerSlot?: React.ReactNode;
  headerEnd?: React.ReactNode;
  /** The most rooms the plan may hold. 1: no Draw tool, the room is created from the form. Unlimited when absent. */
  maxRooms?: number;
  /** Whether Escape, with nothing selected, goes back to 3D (default true). */
  escapeCloses?: boolean;
  /** Whether the row has its own Back to 3D button (default true). */
  backButton?: boolean;
  /** The plan's space type, for the room dialog's picker. */
  spaceType?: string;
  onSpaceType?: (id: string) => void;
  /** The header's logo. Trusscore's when absent. */
  brand?: DesignerBrand;
  /** The header's brand row (default true). Off in the Look Lab. */
  brandBar?: boolean;
}

type Drag =
  | { kind: 'pan'; startX: number; startY: number; view: View }
  | { kind: 'move'; startX: number; startY: number; ids: string[]; grabbed: PlanRect; additive: boolean; id: string }
  | { kind: 'resize'; startX: number; startY: number; id: string; handle: HandleId; orig: PlanRect }
  | { kind: 'draw'; startX: number; startY: number; ax: number; az: number };

const chip = (active: boolean): React.CSSProperties => ({
  height: 28, padding: '0 10px', borderRadius: 6, cursor: 'pointer',
  fontSize: '0.8rem', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap',
  display: 'flex', alignItems: 'center', gap: 6,
  background: active ? 'rgba(254,177,0,0.18)' : 'rgba(255,255,255,0.08)',
  border: active ? `1px solid ${SELECT}` : '1px solid rgba(255,255,255,0.25)',
});

/** A point on a wall: `along` it (sheet coordinate) and `into` the room from its inside face. */
function wallPoint(room: PlanRect, face: PlanFace, along: number, into: number): [number, number] {
  switch (face) {
    case 'front': return [along, room.z + into];
    case 'back': return [along, room.z + room.width - into];
    case 'left': return [room.x + into, along];
    case 'right': return [room.x + room.length - into, along];
  }
}

export const FloorPlanner: React.FC<FloorPlannerProps> = ({
  plan, onCommit, onUndo, onRedo, canUndo = false, canRedo = false,
  focusRoomId, onFocusRoom, onClose, headerSlot, headerEnd,
  maxRooms, escapeCloses = true, backButton = true, spaceType = 'garage', onSpaceType, brand, brandBar = true,
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [view, setView] = useState<View>({ scale: 16, ox: 80, oy: 80 });
  const phone = usePhoneSized();
  const phoneRef = useRef(phone); phoneRef.current = phone;
  // Whether rooms are drawn at all (a one-room plan is made from the form), and whether one more may be.
  const drawTool = maxRooms === undefined || maxRooms > 1;
  const canDraw = drawTool && (maxRooms === undefined || plan.rooms.length < maxRooms);
  const canDrawRef = useRef(canDraw); canDrawRef.current = canDraw;
  const [tool, setTool] = useState<Tool>(plan.rooms.length === 0 && canDraw ? 'draw' : 'select');
  const [selectedIds, setSelectedIds] = useState<string[]>(() => (plan.rooms.some((r) => r.id === focusRoomId) && focusRoomId ? [focusRoomId] : []));
  // What a drag in progress would do: rooms shown somewhere else, or a new room.
  const [moved, setMoved] = useState<Record<string, PlanRect> | null>(null);
  const [drawing, setDrawing] = useState<{ rect: PlanRect; ok: boolean } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The room has just come out of the wizard: the way on to 3D draws the eye for a moment.
  const [justCreated, setJustCreated] = useState(false);
  // The room editor, open on one of its parts.
  const [editing, setEditing] = useState<RoomStep | null>(null);
  // A shared wall picked on the sheet (the pair of rooms it is between), instead of a room.
  const [selectedWall, setSelectedWall] = useState<string | null>(null);
  // Removing a whole wall that has windows or doors on it asks first; and a row of the list can be pointed at.
  const [confirmWall, setConfirmWall] = useState<string | null>(null);
  const [hoverLink, setHoverLink] = useState<string | null>(null);
  // A window or door picked on a shared wall — by where it is (which copy of it,
  // and whether it has a twin yet, can change when the wall behind it is opened).
  const [pickedOpening, setPickedOpening] = useState<string | null>(null);

  // Latest values for handlers that outlive a render (window listeners during a drag).
  const planRef = useRef(plan); planRef.current = plan;
  const viewRef = useRef(view); viewRef.current = view;
  const selectedRef = useRef(selectedIds); selectedRef.current = selectedIds;
  const cancelDragRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  // A selection can outlive its rooms (undo of an add, redo of a delete).
  useEffect(() => {
    setSelectedIds((cur) => (cur.every((id) => plan.rooms.some((r) => r.id === id)) ? cur : cur.filter((id) => plan.rooms.some((r) => r.id === id))));
  }, [plan]);
  // The editor is the selection's: it closes with it.
  useEffect(() => { if (selectedIds.length === 0) setEditing(null); }, [selectedIds]);

  // ---------- Sheet size and view ----------
  useLayoutEffect(() => {
    const el = sheetRef.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(el);
    return () => observer?.disconnect();
  }, []);

  // The rooms in view, with room around them for the toolbar — and, on a
  // phone, clear of the Design in 3D button at the top and with the toolbar's
  // place below.
  const fit = useCallback(() => {
    const el = sheetRef.current;
    if (!el) return;
    const reserveTop = phoneRef.current ? CTA_SPACE_PX : 0;
    const reserveBottom = phoneRef.current ? PHONE_TOOLBAR_PX : 0;
    const w = el.clientWidth;
    const h = el.clientHeight - reserveTop - reserveBottom;
    const bounds = planBounds(planRef.current);
    if (!bounds) { setView({ scale: 16, ox: w / 2 - 160, oy: reserveTop + h / 2 - 120 }); return; }
    const margin = Math.min(110, w / 6, h / 6);
    const scale = Math.max(MIN_SCALE, Math.min(28, (w - 2 * margin) / bounds.length, (h - 2 * margin) / bounds.width));
    setView({
      scale,
      ox: w / 2 - (bounds.x + bounds.length / 2) * scale,
      oy: reserveTop + h / 2 - (bounds.z + bounds.width / 2) * scale,
    });
  }, []);

  const fittedRef = useRef(false);
  useLayoutEffect(() => {
    if (fittedRef.current || size.w === 0) return;
    fittedRef.current = true;
    fit();
  }, [size.w, fit]);
  // A room just created from the form: fit once the plan with it has rendered.
  const fitAfterRef = useRef(false);
  useLayoutEffect(() => {
    if (!fitAfterRef.current) return;
    fitAfterRef.current = false;
    fit();
  }, [plan, fit]);

  const px = useCallback((x: number, z: number): [number, number] => [view.ox + x * view.scale, view.oy + z * view.scale], [view]);

  // Wheel zoom about the pointer (needs a non-passive listener to stop the page scrolling).
  useEffect(() => {
    const el = sheetRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      setView((v) => {
        const scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, v.scale * Math.pow(1.0015, -e.deltaY)));
        const k = scale / v.scale;
        return { scale, ox: mx - (mx - v.ox) * k, oy: my - (my - v.oy) * k };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // ---------- Edits ----------
  const refuse = useCallback((reason: string) => setNotice(P.refused[reason] ?? S.refused[reason] ?? reason), []);
  const apply = useCallback((result: PlanResult, key?: string): boolean => {
    if (!result.ok) { refuse(result.reason); return false; }
    setNotice(null);
    onCommit(result.plan, key);
    return true;
  }, [onCommit, refuse]);

  const selectedRooms = useMemo(() => plan.rooms.filter((r) => selectedIds.includes(r.id)), [plan, selectedIds]);
  const sole = selectedRooms.length === 1 ? selectedRooms[0] : null;

  const removeSelected = useCallback(() => {
    if (selectedRef.current.length === 0) return;
    if (apply(deleteRooms(planRef.current, selectedRef.current))) setSelectedIds([]);
  }, [apply]);

  // ---------- Dragging ----------
  const beginDrag = useCallback((e: React.PointerEvent, drag: Drag) => {
    const el = sheetRef.current;
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    const box = el.getBoundingClientRect();
    const feet = (clientX: number, clientY: number): [number, number] => {
      const v = viewRef.current;
      return [(clientX - box.left - v.ox) / v.scale, (clientY - box.top - v.oy) / v.scale];
    };
    const snapFor = (ev: PointerEvent) => (ev.altKey
      ? { grid: INCH, tol: 0 }
      : { grid: GRID_FT, tol: SNAP_PX / viewRef.current.scale });
    let travelled = false;
    // The last place the drag was allowed to be.
    let lastDelta: [number, number] = [0, 0];
    let lastRect: PlanRect | null = null;
    let lastDraw: { rect: PlanRect; ok: boolean } | null = null;

    const onMove = (ev: PointerEvent) => {
      if (!travelled && Math.hypot(ev.clientX - drag.startX, ev.clientY - drag.startY) < CLICK_PX) return;
      travelled = true;
      const p = planRef.current;
      const v = viewRef.current;
      const dxFt = (ev.clientX - drag.startX) / v.scale;
      const dzFt = (ev.clientY - drag.startY) / v.scale;

      if (drag.kind === 'pan') {
        setView({ ...drag.view, ox: drag.view.ox + ev.clientX - drag.startX, oy: drag.view.oy + ev.clientY - drag.startY });
      } else if (drag.kind === 'move') {
        const still = p.rooms.filter((r) => !drag.ids.includes(r.id));
        const snapped = snapMove({ ...drag.grabbed, x: drag.grabbed.x + dxFt, z: drag.grabbed.z + dzFt }, snapLinesFor(still), snapFor(ev));
        const delta: [number, number] = [snapped.x - drag.grabbed.x, snapped.z - drag.grabbed.z];
        const rects: Record<string, PlanRect> = {};
        let allowed = true;
        for (const room of p.rooms.filter((r) => drag.ids.includes(r.id))) {
          rects[room.id] = { ...rectOfRoom(room), x: room.x + delta[0], z: room.z + delta[1] };
          if (placementIssue(still, rects[room.id])) allowed = false;
        }
        if (allowed) { lastDelta = delta; setMoved(rects); }
      } else if (drag.kind === 'resize') {
        const edges = edgesOf(drag.handle);
        const o = drag.orig;
        const min = ROOM_LIMITS.side.min;
        let x0 = o.x, x1 = o.x + o.length, z0 = o.z, z1 = o.z + o.width;
        if (edges.left) x0 = Math.min(x0 + dxFt, x1 - min);
        if (edges.right) x1 = Math.max(x1 + dxFt, x0 + min);
        if (edges.front) z0 = Math.min(z0 + dzFt, z1 - min);
        if (edges.back) z1 = Math.max(z1 + dzFt, z0 + min);
        const still = p.rooms.filter((r) => r.id !== drag.id);
        const rect = snapResize({ x: x0, z: z0, length: x1 - x0, width: z1 - z0 }, edges, snapLinesFor(still), snapFor(ev));
        if (!placementIssue(still, rect)) { lastRect = rect; setMoved({ [drag.id]: rect }); }
      } else {
        const [cx, cz] = feet(ev.clientX, ev.clientY);
        const raw = { x: Math.min(drag.ax, cx), z: Math.min(drag.az, cz), length: Math.abs(cx - drag.ax), width: Math.abs(cz - drag.az) };
        const edges: MovingEdges = { left: cx < drag.ax, right: cx >= drag.ax, front: cz < drag.az, back: cz >= drag.az };
        const rect = snapResize(raw, edges, snapLinesFor(p.rooms), snapFor(ev));
        lastDraw = { rect, ok: !placementIssue(p.rooms, rect) };
        setDrawing(lastDraw);
      }
    };

    const finish = (commit: boolean) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      cancelDragRef.current = null;
      setMoved(null);
      setDrawing(null);
      if (!commit) return;
      const p = planRef.current;

      if (!travelled) {
        // A click.
        if (drag.kind === 'move') {
          onFocusRoom(drag.id);
          setSelectedWall(null);
          setSelectedIds((cur) => (drag.additive ? (cur.includes(drag.id) ? cur.filter((id) => id !== drag.id) : [...cur, drag.id]) : [drag.id]));
        } else if (drag.kind === 'pan') {
          setSelectedIds([]);
          setSelectedWall(null);
        }
        return;
      }
      if (drag.kind === 'move' && (lastDelta[0] !== 0 || lastDelta[1] !== 0)) {
        apply(moveRooms(p, drag.ids, lastDelta[0], lastDelta[1]));
      } else if (drag.kind === 'resize' && lastRect) {
        apply(setRoomRect(p, drag.id, lastRect));
      } else if (drag.kind === 'draw' && lastDraw) {
        if (!canDrawRef.current) { refuse('room-limit'); return; }
        if (!lastDraw.ok) { refuse(placementIssue(p.rooms, lastDraw.rect) ?? 'overlap'); return; }
        const added = addRoom(p, lastDraw.rect, heightForNewRoom(p, focusRoomId));
        if (apply(added) && added.ok) {
          setSelectedIds([added.roomId]);
          onFocusRoom(added.roomId);
          setTool('select');
        }
      }
    };
    const onUp = () => finish(true);
    const onCancel = () => finish(false);
    cancelDragRef.current = () => finish(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
  }, [apply, refuse, onFocusRoom, focusRoomId]);

  // The room made by the wizard (a one-room plan): sized, shaped, finished, its space set — one commit, one undo step.
  const createRoom = useCallback((form: CreateRoomForm) => {
    const p = planRef.current;
    const added = addRoom(p, { x: 0, z: 0, length: form.length, width: form.width }, form.height, form.name);
    if (!added.ok) { refuse(added.reason); return; }
    let next = added.plan;
    if (form.ceilingProfile && form.ceilingProfile.kind !== 'flat') {
      const shaped = setCeilingProfile(next, [added.roomId], form.ceilingProfile);
      if (shaped.ok) next = shaped.plan;
      else refuse(shaped.reason);
    }
    if (form.finish) {
      const finished = applyStarterFinish(next, added.roomId, form.finish);
      if (finished.ok) next = finished.plan;
    }
    next = withStaging(next, undefined, form.spaceType);
    setNotice(null);
    onCommit(next);
    setSelectedIds([added.roomId]);
    onFocusRoom(added.roomId);
    setTool('select');
    setJustCreated(true);
    fitAfterRef.current = true;
  }, [onCommit, onFocusRoom, refuse]);

  const onSheetDown = (e: React.PointerEvent) => {
    if (e.button === 1 || (e.button === 0 && (tool === 'select' || !canDraw))) {
      beginDrag(e, { kind: 'pan', startX: e.clientX, startY: e.clientY, view });
    } else if (e.button === 0 && tool === 'draw') {
      const el = sheetRef.current!;
      const box = el.getBoundingClientRect();
      // The corner the room is drawn from lands on a wall line or the grid too.
      const anchor = snapPoint(
        (e.clientX - box.left - view.ox) / view.scale, (e.clientY - box.top - view.oy) / view.scale,
        snapLinesFor(plan.rooms), e.altKey ? { grid: INCH, tol: 0 } : { grid: GRID_FT, tol: SNAP_PX / view.scale },
      );
      beginDrag(e, { kind: 'draw', startX: e.clientX, startY: e.clientY, ax: anchor.x, az: anchor.z });
    }
  };

  const onRoomDown = (e: React.PointerEvent, room: PlanRoom) => {
    if (e.button !== 0) return;
    if (tool === 'draw') { onSheetDown(e); return; }
    const additive = e.shiftKey || e.ctrlKey || e.metaKey;
    setSelectedWall(null);
    // Dragging a room that is part of the selection moves the whole selection.
    const ids = selectedIds.includes(room.id) && !additive ? selectedIds : [room.id];
    beginDrag(e, { kind: 'move', startX: e.clientX, startY: e.clientY, ids, grabbed: rectOfRoom(room), additive, id: room.id });
  };

  // ---------- Keyboard ----------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      // The room editor open: Escape closes it, and no key reaches the rooms behind it.
      if (editing) {
        if (e.key === 'Escape') { e.preventDefault(); setEditing(null); }
        return;
      }
      if (e.key === 'Escape') {
        if (typing) { el?.blur(); return; }
        if (cancelDragRef.current) cancelDragRef.current();
        else if (tool === 'draw' && plan.rooms.length > 0) setTool('select');
        else if (selectedWall) setSelectedWall(null);
        else if (selectedIds.length) setSelectedIds([]);
        else if (plan.rooms.length > 0 && escapeCloses) onClose();
        return;
      }
      if (typing) return;
      if (mod && key === 'z') { e.preventDefault(); if (e.shiftKey) onRedo?.(); else onUndo?.(); return; }
      if (mod && key === 'y') { e.preventDefault(); onRedo?.(); return; }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.length) { e.preventDefault(); removeSelected(); return; }
      const arrow = ({ ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] } as Record<string, [number, number]>)[e.key];
      if (arrow && selectedIds.length) {
        e.preventDefault();
        const step = mod ? INCH : e.shiftKey ? 1 : GRID_FT;
        apply(nudgeRooms(plan, selectedIds, arrow[0] * step, arrow[1] * step), `${selectedIds.join('+')}:nudge`);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [plan, tool, selectedIds, selectedWall, editing, apply, removeSelected, onClose, onUndo, onRedo, escapeCloses]);

  // ---------- Drawing helpers ----------
  const shown = (room: PlanRoom): PlanRect => moved?.[room.id] ?? room;

  const renderOpening = (room: PlanRect, face: PlanFace, o: Opening, key: string) => {
    const a0 = sheetAlong(room, face, o.x);
    const a1 = sheetAlong(room, face, o.x + o.width);
    const at = (along: number, into: number) => px(...wallPoint(room, face, along, into));
    const line = (p: [number, number], q: [number, number], extra: React.SVGProps<SVGLineElement> = {}) => (
      <line x1={p[0]} y1={p[1]} x2={q[0]} y2={q[1]} stroke={LINE} strokeWidth={1.25} {...extra} />
    );
    // Clear the wall where the opening is, and mark its ends.
    const corners = [at(a0, 0), at(a1, 0), at(a1, -WALL_GAP_FT), at(a0, -WALL_GAP_FT)];
    const parts: React.ReactNode[] = [
      <polygon key="gap" points={corners.map((c) => c.join(',')).join(' ')} fill={SHEET} stroke="none" />,
      <g key="ends">{line(at(a0, 0), at(a0, -WALL_GAP_FT))}{line(at(a1, 0), at(a1, -WALL_GAP_FT))}</g>,
    ];
    if (o.kind === 'window') {
      parts.push(<g key="glass">{[0, -WALL_GAP_FT / 2, -WALL_GAP_FT].map((d) => <React.Fragment key={d}>{line(at(a0, d), at(a1, d), { strokeWidth: 1 })}</React.Fragment>)}</g>);
    } else if (o.kind === 'door') {
      // The leaf standing open, on its hinges, and the quarter-circle it swings
      // through — into this room, or out through the far face of the wall.
      const hingeAt = doorHinge(o) === 'left' ? a0 : a1;
      const latchAt = doorHinge(o) === 'left' ? a1 : a0;
      const face0 = doorSwing(o) === 'in' ? 0 : -WALL_GAP_FT;
      const dir = doorSwing(o) === 'in' ? 1 : -1;
      const swing: string[] = [];
      for (let i = 0; i <= 12; i++) {
        const t = (i / 12) * (Math.PI / 2);
        swing.push(at(hingeAt + (latchAt - hingeAt) * Math.sin(t), face0 + dir * o.width * Math.cos(t)).join(','));
      }
      parts.push(<g key="door">{line(at(hingeAt, face0), at(hingeAt, face0 + dir * o.width), { strokeWidth: 1.75 })}<polyline points={swing.join(' ')} fill="none" stroke={LINE_SOFT} strokeWidth={1} strokeDasharray="3 3" /></g>);
    } else if (o.kind === 'garageDoor') {
      parts.push(<g key="garage">{line(at(a0, -WALL_GAP_FT / 2), at(a1, -WALL_GAP_FT / 2), { strokeWidth: 2, strokeDasharray: '7 4' })}</g>);
    }
    return <g key={key} pointerEvents="none">{parts}</g>;
  };

  /** A dimension line outside one side of a rectangle, with ticks and its length. */
  const renderDimension = (rect: PlanRect, side: 'top' | 'left', color: string) => {
    const off = WALL_GAP_FT * view.scale + 16;
    if (side === 'top') {
      const [x0, y] = px(rect.x, rect.z);
      const [x1] = px(rect.x + rect.length, rect.z);
      const yy = y - off;
      return (
        <g pointerEvents="none" stroke={color} strokeWidth={1} fill={color} fontFamily={FONT} fontSize={12}>
          <line x1={x0} y1={yy} x2={x1} y2={yy} />
          <line x1={x0} y1={yy - 5} x2={x0} y2={yy + 5} />
          <line x1={x1} y1={yy - 5} x2={x1} y2={yy + 5} />
          <text x={(x0 + x1) / 2} y={yy - 6} textAnchor="middle" stroke="none">{formatFeetInches(rect.length)}</text>
        </g>
      );
    }
    const [x, y0] = px(rect.x, rect.z);
    const [, y1] = px(rect.x, rect.z + rect.width);
    const xx = x - off;
    return (
      <g pointerEvents="none" stroke={color} strokeWidth={1} fill={color} fontFamily={FONT} fontSize={12}>
        <line x1={xx} y1={y0} x2={xx} y2={y1} />
        <line x1={xx - 5} y1={y0} x2={xx + 5} y2={y0} />
        <line x1={xx - 5} y1={y1} x2={xx + 5} y2={y1} />
        <text x={xx - 6} y={(y0 + y1) / 2} textAnchor="middle" stroke="none" transform={`rotate(-90 ${xx - 6} ${(y0 + y1) / 2})`}>{formatFeetInches(rect.width)}</text>
      </g>
    );
  };

  const renderRoom = (room: PlanRoom) => {
    const r = shown(room);
    const selected = selectedIds.includes(room.id);
    const [x, y] = px(r.x, r.z);
    const w = r.length * view.scale;
    const h = r.width * view.scale;
    const t = WALL_GAP_FT * view.scale;
    const stroke = selected ? SELECT : LINE;
    const labelSize = Math.max(10, Math.min(15, w / 9));
    // A sloped or peaked ceiling: where its high line runs, in the room's own feet (x, z).
    const geom = room.ceilingProfile && room.ceilingProfile.kind !== 'flat' ? ceilingGeometry(room) : null;
    const peak = geom?.peak ?? room.height;
    const creases: [[number, number], [number, number]][] = (geom?.creases ?? []).map((c) => [[c[0][0], c[0][2]], [c[1][0], c[1][2]]]);
    return (
      <g key={room.id}>
        {/* The wall: a band the thickness of a stud around the inside of the room. */}
        <path
          d={`M${x - t},${y - t}h${w + 2 * t}v${h + 2 * t}h${-(w + 2 * t)}z M${x},${y}v${h}h${w}v${-h}z`}
          fill="rgba(234,242,255,0.2)" fillRule="evenodd" stroke={stroke} strokeWidth={selected ? 1.75 : 1.25} pointerEvents="none"
        />
        <rect
          x={x} y={y} width={w} height={h}
          fill={selected ? 'rgba(254,177,0,0.09)' : 'rgba(234,242,255,0.04)'}
          style={{ cursor: tool === 'draw' ? 'crosshair' : 'move' }}
          onPointerDown={(e) => onRoomDown(e, room)}
          onDoubleClick={() => { if (tool === 'select') { onFocusRoom(room.id); onClose(); } }}
        >
          <title>{room.name}</title>
        </rect>
        {PLAN_FACES.map((face, i) => (room.surfaces.walls[i]?.openings ?? []).map((o) => renderOpening(r, face, o, `${face}-${o.id}`)))}
        {creases.map((c, i) => (
          // Where the ceiling changes slope (a ridge, a shed's high side, a vault's shoulders), dashed as on a drawing.
          <line
            key={`crease-${i}`}
            x1={x + c[0][0] * view.scale} y1={y + c[0][1] * view.scale} x2={x + c[1][0] * view.scale} y2={y + c[1][1] * view.scale}
            stroke={stroke} strokeWidth={1} strokeDasharray="7 4" opacity={0.8} pointerEvents="none"
          />
        ))}
        {w > 70 && h > 44 && (() => {
          // The pencil after the name (monospace: its width is its letters), the two centred together.
          const icon = Math.max(12, labelSize);
          const nameW = Math.min(w - icon - 16, room.name.length * labelSize * 0.68);
          const nameX = x + w / 2 - (icon + 6) / 2;
          const ix = nameX + nameW / 2 + 6;
          const iy = y + h / 2 - 4 - labelSize * 0.78 - (icon - labelSize * 0.78) / 2;
          return (
            <g
              className="fp-rename"
              role="button" aria-label={P.tools.titles.name}
              onPointerDown={(e) => { if (e.button === 0) e.stopPropagation(); }}
              onClick={() => { setSelectedWall(null); setSelectedIds([room.id]); setEditing('name'); }}
            >
              <title>{P.tools.titles.name}</title>
              <rect className="fp-rename-bg" x={ix - 4} y={iy - 4} width={icon + 8} height={icon + 8} rx={5} fill="rgba(255,255,255,0.001)" />
              <PenLine x={ix} y={iy} size={icon} color={selected ? SELECT : LINE} strokeWidth={2} />
            </g>
          );
        })()}
        {w > 70 && h > 44 && (
          <g pointerEvents="none" fontFamily={FONT} fill={selected ? SELECT : LINE} textAnchor="middle">
            <text x={x + w / 2 - (Math.max(12, labelSize) + 6) / 2} y={y + h / 2 - 4} fontSize={labelSize} fontWeight={700} letterSpacing="0.08em">{room.name.toUpperCase()}</text>
            <text x={x + w / 2} y={y + h / 2 + labelSize} fontSize={Math.max(9, labelSize - 3)} opacity={0.85}>
              {formatFeetInches(r.length)} × {formatFeetInches(r.width)}
            </text>
            <text x={x + w / 2} y={y + h / 2 + labelSize * 2 + 1} fontSize={Math.max(9, labelSize - 4)} opacity={0.65}>
              {P.ceiling} {formatFeetInches(room.height)}{peak > room.height + 1e-6 ? ` – ${formatFeetInches(peak)}` : ''}
            </text>
          </g>
        )}
      </g>
    );
  };

  const soleShown = sole ? shown(sole) : null;
  /** The band that stands for a stretch of a shared wall, between two sheet positions along it. */
  const bandRect = (st: { axis: 'x' | 'z'; at: number }, from: number, to: number) => {
    const [x0, y0] = st.axis === 'x' ? px(from, st.at - WALL_GAP_FT / 2) : px(st.at - WALL_GAP_FT / 2, from);
    const along = (to - from) * view.scale;
    const across = Math.max(WALL_GAP_FT * view.scale, 14); // a band wide enough to hit
    const inset = (WALL_GAP_FT * view.scale - across) / 2;
    return st.axis === 'x'
      ? { x: x0, y: y0 + inset, width: along, height: across }
      : { x: x0 + inset, y: y0, width: across, height: along };
  };
  // The shared walls — not while something is being dragged (the rooms are not where they were).
  const stretches = useMemo(() => (moved ? [] : sharedStretches(plan)), [plan, moved]);
  const pickedStretch = selectedWall ? stretches.find((s) => sharedWallKey(s) === selectedWall) : undefined;
  const pickedOpen = !!pickedStretch && isWallOpen(plan, pickedStretch.owner.roomId, pickedStretch.other.roomId);
  const gridPx = view.scale;
  const hint = plan.rooms.length === 0 ? (drawTool ? P.hintEmpty : P.hintCreate) : tool === 'draw' ? P.hintDraw : canDraw ? P.hintSelect : P.hintSelectOne;
  // A blank sheet with no Draw tool: the room is made from the centred form.
  const creating = plan.rooms.length === 0 && !drawTool;
  // The room "Design in 3D" opens: the selected one, else the focus room, else the first.
  const designTarget = sole ?? plan.rooms.find((r) => r.id === focusRoomId) ?? plan.rooms[0];
  // Where the selection is on screen, for its toolbar: the rooms as shown (following a drag).
  const selectionBox = (() => {
    if (selectedRooms.length === 0) return null;
    const rects = selectedRooms.map(shown);
    const x0 = Math.min(...rects.map((r) => r.x)), z0 = Math.min(...rects.map((r) => r.z));
    const x1 = Math.max(...rects.map((r) => r.x + r.length)), z1 = Math.max(...rects.map((r) => r.z + r.width));
    const [sx, sy] = px(x0, z0);
    return { x: sx, y: sy, w: (x1 - x0) * view.scale, h: (z1 - z0) * view.scale };
  })();
  const ctaShown = !drawTool && !creating && !!designTarget;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#1a1a1e' }}>
      {/* The two-row header (chrome/DesignerHeader): the brand, then the
          controls — the planner's own around the host's, dropping to icons
          when the row is short of room. */}
      <DesignerHeader title={P.title} brand={brand} brandBar={brandBar} end={headerEnd}>
        <IconTextButton icon={<MousePointer2 size={14} />} label={P.select} title={P.selectTitle} active={tool === 'select'} onClick={() => setTool('select')} />
        {drawTool && (
          <IconTextButton icon={<PencilRuler size={14} />} label={P.draw} title={P.drawTitle} active={tool === 'draw'} disabled={!canDraw} onClick={() => setTool('draw')} />
        )}
        <IconTextButton icon={<Maximize size={14} />} label={P.fit} title={P.fitTitle} onClick={fit} />
        {headerSlot}
        {(onUndo || onRedo) && (
          <>
            <IconTextButton icon={<Undo2 size={15} />} label={S.undoLabel} title={S.undo} disabled={!canUndo} onClick={onUndo} />
            <IconTextButton icon={<Redo2 size={15} />} label={S.redoLabel} title={S.redo} disabled={!canRedo} onClick={onRedo} />
          </>
        )}
        {backButton && (
          <IconTextButton accent icon={<Box size={14} />} label={P.back} title={P.backTitle} disabled={plan.rooms.length === 0} onClick={onClose} />
        )}
      </DesignerHeader>

      <div
        ref={sheetRef}
        className="floor-planner-sheet"
        onPointerDown={onSheetDown}
        style={{ flex: '1 1 auto', minHeight: 0, position: 'relative', overflow: 'hidden', background: SHEET, cursor: tool === 'draw' ? 'crosshair' : 'grab', touchAction: 'none', userSelect: 'none' }}
      >
        <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, display: 'block' }}>
          <defs>
            <pattern id="fp-grid-1" width={gridPx} height={gridPx} patternUnits="userSpaceOnUse" patternTransform={`translate(${view.ox % gridPx} ${view.oy % gridPx})`}>
              <path d={`M${gridPx} 0H0V${gridPx}`} fill="none" stroke="rgba(234,242,255,0.07)" strokeWidth={1} />
            </pattern>
            <pattern id="fp-grid-5" width={gridPx * 5} height={gridPx * 5} patternUnits="userSpaceOnUse" patternTransform={`translate(${view.ox % (gridPx * 5)} ${view.oy % (gridPx * 5)})`}>
              <path d={`M${gridPx * 5} 0H0V${gridPx * 5}`} fill="none" stroke="rgba(234,242,255,0.16)" strokeWidth={1} />
            </pattern>
          </defs>
          {gridPx >= 6 && <rect width="100%" height="100%" fill="url(#fp-grid-1)" pointerEvents="none" />}
          <rect width="100%" height="100%" fill="url(#fp-grid-5)" pointerEvents="none" />

          {plan.rooms.map(renderRoom)}

          {/* The walls two rooms share: picked on the sheet to remove or restore. */}
          {stretches.map((st) => {
            const key = sharedWallKey(st);
            const open = isWallOpen(plan, st.owner.roomId, st.other.roomId);
            const picked = selectedWall === key;
            const { x: rx, y: ry, width: bandW, height: bandH } = bandRect(st, st.from, st.to);
            return (
              <g key={`wall-${key}`}>
                <rect
                  x={rx} y={ry} width={bandW} height={bandH}
                  fill={picked ? 'rgba(254,177,0,0.35)' : open ? 'rgba(254,177,0,0.12)' : 'transparent'}
                  stroke={picked ? SELECT : open ? 'rgba(254,177,0,0.8)' : 'none'} strokeWidth={1.25} strokeDasharray={open ? '5 4' : undefined}
                  style={{ cursor: tool === 'select' ? 'pointer' : 'crosshair' }}
                  onPointerDown={(e) => {
                    if (e.button !== 0 || tool !== 'select') { if (tool === 'draw') onSheetDown(e); return; }
                    e.stopPropagation();
                    setSelectedIds([]);
                    setSelectedWall(key);
                    setPickedOpening(null);
                  }}
                >
                  <title>{P.sharedWall}</title>
                </rect>
                {/* The wall out behind one window or door, and the one a list row points at. */}
                {plan.openWalls.filter((w) => w.from !== undefined && [w.a, w.b].sort().join('|') === key).map((w) => {
                  const r = bandRect(st, w.from!, w.to ?? st.to);
                  return <rect key={`void-${w.link}`} {...r} fill="rgba(254,177,0,0.3)" stroke="rgba(254,177,0,0.9)" strokeWidth={1.25} strokeDasharray="4 3" pointerEvents="none" />;
                })}
                {tool === 'select' && openingsInStretch(plan, st).map((e) => {
                  const r = bandRect(st, e.from, e.to);
                  const pad = 3;
                  const id = spotOf(e);
                  const chosen = picked && pickedOpening === id;
                  const kind = S.openingName[e.opening.kind] ?? e.opening.kind;
                  return (
                    <rect
                      key={`pick-${id}`}
                      x={r.x - pad} y={r.y - pad} width={r.width + 2 * pad} height={r.height + 2 * pad}
                      fill={chosen ? 'rgba(255,107,94,0.3)' : 'rgba(255,255,255,0.001)'}
                      stroke={chosen ? BAD : 'none'} strokeWidth={2}
                      style={{ cursor: 'pointer' }}
                      onPointerDown={(ev) => {
                        if (ev.button !== 0) return;
                        ev.stopPropagation();
                        setSelectedIds([]);
                        setSelectedWall(key);
                        setPickedOpening(id);
                        setConfirmWall(null);
                      }}
                    >
                      <title>{P.pickOpeningTitle(kind)}</title>
                    </rect>
                  );
                })}
                {picked && hoverLink && (() => {
                  const e = openingsInStretch(plan, st).find((o) => o.opening.link === hoverLink);
                  if (!e) return null;
                  const r = bandRect(st, e.from, e.to);
                  return <rect {...r} fill="none" stroke={BAD} strokeWidth={2} pointerEvents="none" />;
                })()}
              </g>
            );
          })}

          {/* The selected room's size, and its handles. */}
          {soleShown && renderDimension(soleShown, 'top', SELECT)}
          {soleShown && renderDimension(soleShown, 'left', SELECT)}
          {sole && soleShown && tool === 'select' && HANDLES.map((hd) => {
            const [hx, hy] = px(soleShown.x + soleShown.length * hd.fx, soleShown.z + soleShown.width * hd.fz);
            return (
              <rect
                key={hd.id} x={hx - HANDLE_PX / 2} y={hy - HANDLE_PX / 2} width={HANDLE_PX} height={HANDLE_PX}
                fill={SELECT} stroke="#17181c" strokeWidth={1} style={{ cursor: hd.cursor }}
                onPointerDown={(e) => { if (e.button === 0) beginDrag(e, { kind: 'resize', startX: e.clientX, startY: e.clientY, id: sole.id, handle: hd.id, orig: rectOfRoom(sole) }); }}
              />
            );
          })}

          {/* A room being drawn. */}
          {drawing && (() => {
            const [x, y] = px(drawing.rect.x, drawing.rect.z);
            const color = drawing.ok ? LINE : BAD;
            return (
              <g pointerEvents="none">
                <rect x={x} y={y} width={drawing.rect.length * view.scale} height={drawing.rect.width * view.scale} fill={drawing.ok ? 'rgba(234,242,255,0.1)' : 'rgba(255,107,94,0.18)'} stroke={color} strokeWidth={1.5} strokeDasharray="6 4" />
                {renderDimension(drawing.rect, 'top', color)}
                {renderDimension(drawing.rect, 'left', color)}
              </g>
            );
          })()}
        </svg>

        {/* Title block, as on a drawing. */}
        <div style={{ position: 'absolute', left: 14, bottom: 14, border: `1px solid ${LINE_SOFT}`, color: LINE, fontFamily: FONT, fontSize: 11, letterSpacing: '0.08em', pointerEvents: 'none', background: 'rgba(21,62,110,0.85)' }}>
          <div style={{ padding: '4px 10px', borderBottom: `1px solid ${LINE_SOFT}`, fontWeight: 700 }}>{P.sheetTitle}</div>
          <div style={{ padding: '4px 10px', display: 'flex', gap: 14 }}><span>{P.rooms(plan.rooms.length)}</span><span>{P.grid}</span></div>
          <div style={{ padding: '4px 10px 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ display: 'inline-block', width: 5 * view.scale, maxWidth: 220, height: 5, borderLeft: `1px solid ${LINE}`, borderRight: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}` }} />
            <span>5 FT</span>
          </div>
        </div>

        {/* A blank sheet with no Draw tool: create the room in the wizard. */}
        {creating && (
          <CreateRoomWizard defaultName={`Room ${plan.rooms.length + 1}`} spaceType={spaceType} onCreate={createRoom} />
        )}

        {/* One room, on the blueprint: the way on to 3D, where it can't be missed. */}
        {ctaShown && (
          <button
            type="button"
            className={`dh-btn is-accent fp-cta${justCreated ? ' is-new' : ''}`}
            title={P.designIn3dTitle}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => { onFocusRoom(designTarget.id); onClose(); }}
            style={phone ? { top: 12, left: '50%', transform: 'translateX(-50%)' } : { bottom: 22, left: '50%', transform: 'translateX(-50%)' }}
          >
            <Box size={17} /> {P.designIn3d} <ArrowRight size={16} />
          </button>
        )}

        {/* Nothing selected: what to do, in a line. */}
        {!creating && selectedRooms.length === 0 && !pickedStretch && !notice && (
          <div className="fp-hint" style={phone ? { bottom: 14 } : { top: 14 }}>{hint}</div>
        )}

        {/* The selected room's tools, beside it. */}
        {!creating && selectionBox && tool === 'select' && !editing && (
          <RoomToolbar
            room={selectionBox}
            wall={WALL_GAP_FT * view.scale}
            sheet={size}
            keepTop={ctaShown && phone ? CTA_SPACE_PX : 0}
            keepBottom={ctaShown && !phone ? CTA_SPACE_PX + 14 : 0}
            steps={roomSteps(selectedRooms.length, !!onSpaceType).filter((s) => s !== 'name')}
            onPick={setEditing}
            onDelete={removeSelected}
            deleteLabel={sole ? P.delete : P.deleteMany(selectedRooms.length)}
            dragging={!!moved}
          />
        )}

        {/* A shared wall picked: its panel at the top right (a sheet across the bottom on a phone). */}
        {!creating && selectedRooms.length === 0 && pickedStretch && (
        <div
          className="designer-toolbar"
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', boxSizing: 'border-box', overflowY: 'auto', padding: '12px 14px', borderRadius: 10,
            background: 'rgba(26,26,30,0.92)', color: '#fff', border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 6px 20px rgba(0,0,0,0.35)', fontSize: 12, cursor: 'default',
            ...(phone
              ? { left: 8, right: 8, bottom: 8, maxHeight: `${PHONE_PANEL_FRACTION * 100}%` }
              : { right: 14, top: 14, width: PANEL_WIDTH, maxHeight: 'calc(100% - 28px)' }),
          }}
        >
          {(() => {
            const a = plan.rooms.find((r) => r.id === pickedStretch.owner.roomId);
            const b = plan.rooms.find((r) => r.id === pickedStretch.other.roomId);
            const here = openingsInStretch(plan, pickedStretch);
            const confirming = confirmWall === selectedWall && here.length > 0 && !pickedOpen;
            const small: React.CSSProperties = { ...chip(false), padding: '3px 8px', fontSize: 11 };
            const one = pickedOpening ? here.find((e) => spotOf(e) === pickedOpening) : undefined;
            if (one && !pickedOpen) {
              const kind = S.openingName[one.opening.kind] ?? one.opening.kind;
              return (
                <>
                  <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: 13 }}>{P.openingOnWall(kind)}</p>
                  <p style={{ margin: '0 0 8px', color: '#c9cbd3' }}>{P.openingBetween(a?.name ?? '', b?.name ?? '', `${formatFeetInches(one.opening.width)} × ${formatFeetInches(one.opening.height)}`)}</p>
                  <p style={{ margin: '0 0 12px', color: '#a9abb3', lineHeight: 1.45 }}>{one.wallOpen ? P.openBehindNote : P.removeBehindNote(kind)}</p>
                  <button
                    type="button"
                    onClick={() => (one.wallOpen && one.opening.link
                      ? apply({ ok: true, plan: closeWallBehind(plan, one.opening.link) })
                      : apply(openWallBehind(plan, one.roomId, one.opening.id)))}
                    style={{ ...chip(!one.wallOpen), width: '100%', justifyContent: 'center', marginBottom: 8 }}
                  >
                    {one.wallOpen ? P.restoreBehind : P.removeBehind(kind)}
                  </button>
                  <button type="button" onClick={() => setPickedOpening(null)} style={{ ...small, width: '100%', justifyContent: 'center' }}>{P.wholeWall}</button>
                </>
              );
            }
            return (
              <>
                <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: 13 }}>{P.sharedWall}</p>
                <p style={{ margin: '0 0 8px', color: '#c9cbd3' }}>{P.sharedWallBetween(a?.name ?? '', b?.name ?? '', formatFeetInches(pickedStretch.to - pickedStretch.from))}</p>
                <p style={{ margin: '0 0 12px', color: '#a9abb3', lineHeight: 1.45 }}>{pickedOpen ? P.wallRemoved : P.wallStands}</p>
                {!pickedOpen && here.length > 0 && (
                  <div style={{ margin: '0 0 12px' }}>
                    <p style={{ margin: '0 0 6px', fontWeight: 700 }}>{P.openingsHere}</p>
                    {here.map((e) => (
                      <div
                        key={e.opening.id}
                        onClick={() => setPickedOpening(spotOf(e))}
                        onPointerEnter={() => setHoverLink(e.opening.link ?? null)}
                        onPointerLeave={() => setHoverLink(null)}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderTop: '1px solid rgba(255,255,255,0.1)' }}
                      >
                        <span style={{ flex: 1 }}>
                          {S.openingName[e.opening.kind] ?? e.opening.kind} · {formatFeetInches(e.opening.width)} × {formatFeetInches(e.opening.height)}
                          {e.wallOpen && <span style={{ display: 'block', color: '#a9abb3', fontSize: 11, lineHeight: 1.35 }}>{P.openBehindNote}</span>}
                        </span>
                        <button
                          type="button"
                          title={e.wallOpen ? undefined : P.openBehindHint}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            if (e.wallOpen && e.opening.link) apply({ ok: true, plan: closeWallBehind(plan, e.opening.link) });
                            else apply(openWallBehind(plan, e.roomId, e.opening.id));
                          }}
                          style={small}
                        >
                          {e.wallOpen ? P.closeBehind : P.openBehind}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {confirming ? (
                  <>
                    <p style={{ margin: '0 0 8px', color: '#ffb4ab', lineHeight: 1.45 }}>{P.removeWarning(here.length)}</p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => { setConfirmWall(null); apply(toggleOpenWall(plan, pickedStretch.owner.roomId, pickedStretch.other.roomId)); }}
                        style={{ ...chip(true), flex: 1, justifyContent: 'center' }}
                      >
                        {P.removeAnyway}
                      </button>
                      <button type="button" onClick={() => setConfirmWall(null)} style={{ ...chip(false), justifyContent: 'center' }}>{P.keepOne}</button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      if (!pickedOpen && here.length > 0) setConfirmWall(selectedWall);
                      else apply(toggleOpenWall(plan, pickedStretch.owner.roomId, pickedStretch.other.roomId));
                    }}
                    style={{ ...chip(false), width: '100%', justifyContent: 'center' }}
                  >
                    {pickedOpen ? P.restoreWall : P.removeWall}
                  </button>
                )}
              </>
            );
          })()}
        </div>
        )}

        {editing && selectedRooms.length > 0 && (
          <RoomEditDialog
            key={selectedIds.join('+')}
            plan={plan}
            rooms={selectedRooms}
            step={editing}
            onStep={setEditing}
            onClose={() => {
              setEditing(null);
              // Grown past the edge of the sheet (a bigger size, say): bring it all back into view.
              const b = selectionBox;
              if (b && (b.x < 0 || b.y < 0 || b.x + b.w > size.w || b.y + b.h > size.h)) fit();
            }}
            apply={apply}
            spaceType={spaceType}
            onSpaceType={onSpaceType}
          />
        )}

        {notice && (
          <div role="status" style={{ position: 'absolute', left: '50%', bottom: ctaShown && !phone ? 76 : 18, zIndex: 7, transform: 'translateX(-50%)', padding: '7px 14px', borderRadius: 8, background: 'rgba(26,26,30,0.94)', color: '#ffd9a8', fontSize: 13, border: '1px solid rgba(255,255,255,0.14)', pointerEvents: 'none' }}>
            {notice}
          </div>
        )}
      </div>
    </div>
  );
};

export default FloorPlanner;
